if (!customElements.get('media-gallery')) {
  // Hàm cuộn nhanh tuỳ chỉnh, thay cho scrollTo({behavior:'smooth'}) mặc định của trình duyệt
  // (trình duyệt không cho set thời lượng smooth scroll qua CSS, nên phải tự viết bằng JS)
  function fastScrollTo(container, { left, top } = {}, duration = 200) {
    if (!container) return;
    const startLeft = container.scrollLeft;
    const startTop = container.scrollTop;
    const targetLeft = typeof left === 'number' ? left : startLeft;
    const targetTop = typeof top === 'number' ? top : startTop;
    const diffLeft = targetLeft - startLeft;
    const diffTop = targetTop - startTop;

    if (diffLeft === 0 && diffTop === 0) return;

    // Dùng lại đúng class .force-instant-jump có sẵn trong theme (product-media-gallery.liquid)
    // để tắt scroll-snap-type + scroll-behavior + overflow-x tạm thời bằng !important.
    // Set riêng lẻ qua style.setProperty(...,'important') không đủ tin cậy trong mọi trường hợp,
    // còn class này đã được kiểm chứng hoạt động đúng khi click thumbnail.
    const hadClass = container.classList.contains('force-instant-jump');
    if (!hadClass) container.classList.add('force-instant-jump');

    // duration <= 0: nhảy thẳng tới ảnh đích luôn, không trượt qua các ảnh ở giữa
    if (duration <= 0) {
      container.scrollLeft = targetLeft;
      container.scrollTop = targetTop;
      void container.offsetWidth; // ép trình duyệt vẽ lại ngay lập tức
      if (!hadClass) container.classList.remove('force-instant-jump');
      return;
    }

    const startTime = performance.now();
    // ease-out: bắt đầu nhanh, chậm dần lúc gần tới đích
    const easeOutQuad = (t) => 1 - (1 - t) * (1 - t);

    function step(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutQuad(progress);
      container.scrollLeft = startLeft + diffLeft * eased;
      container.scrollTop = startTop + diffTop * eased;
      if (progress < 1) {
        requestAnimationFrame(step);
      } else if (!hadClass) {
        container.classList.remove('force-instant-jump');
      }
    }
    requestAnimationFrame(step);
  }

  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            fastScrollTo(activeMedia.parentElement, { left: activeMedia.offsetLeft }, 0);
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        fastScrollTo(this.elements.thumbnails.slider, { left: thumbnail.offsetLeft }, 200);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}